import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Eye, EyeOff } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/37195567/pexels-photo-37195567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    const next = new URLSearchParams(window.location.search).get("next");
    if (ok) navigate(next && next.startsWith("/presenca/") ? next : "/");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      <div
        className="hidden md:flex relative items-end p-12"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.95)), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="kw-fade-up">
          <div className="kw-overline mb-3">Edição 2.0</div>
          <h1 className="font-display text-5xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tighter">
            KW <span className="text-[#E50000]">Lead Growth</span><br /> League
          </h1>
          <p className="mt-6 max-w-md text-[#A3A3A3] text-base">
            Coopetimos para crescer. Não para ganhar a qualquer custo.
            <br />— A regra de ouro.
          </p>
          <div className="mt-8 border-l-2 border-[#E50000] pl-4 space-y-2 max-w-md" data-testid="login-glossary">
            <div className="kw-overline">O que é coopetir?</div>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              <strong className="text-white">Coopetir</strong> é competir e cooperar ao mesmo tempo: medimos-nos
              uns com os outros para subir o nível, e partilhamos o que funciona para todos subirem com ele.
              <br />Ganha o clube que crescer mais em conjunto. Nunca à custa de ninguém.
            </p>
          </div>
          <div className="mt-6 flex items-center gap-2 text-[#A3A3A3] text-sm">
            <Trophy className="w-4 h-4 text-[#E50000]" /> 13 semanas · 2 de preparação + 10 de coopetição + celebração
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 kw-bg-grid">
        <div className="w-full max-w-sm space-y-5">
          <div className="md:hidden border-l-2 border-[#E50000] pl-4" data-testid="login-glossary-mobile">
            <div className="kw-overline mb-1">O que é coopetir?</div>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Competir e cooperar ao mesmo tempo: medimo-nos para subir o nível e partilhamos o que funciona.
              Ganha o clube que crescer mais em conjunto.
            </p>
          </div>
        <form onSubmit={submit} className="w-full max-w-sm kw-card p-8 kw-fade-up" data-testid="login-form">
          <div className="kw-overline mb-2">Acesso</div>
          <h2 className="font-display text-3xl font-black uppercase mb-8 tracking-tight">Iniciar sessão</h2>

          <label className="kw-overline block mb-2">Email</label>
          <input
            data-testid="login-email"
            type="email"
            className="kw-input mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@kwgrowth.pt"
            required
          />

          <label className="kw-overline block mb-2">Password</label>
          <div className="relative mb-4">
            <input
              data-testid="login-password"
              type={showPassword ? "text" : "password"}
              className="kw-input !pr-11 w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute inset-y-0 right-3 flex items-center text-[#737373] hover:text-white"
              tabIndex={-1}
              aria-label={showPassword ? "Ocultar password" : "Mostrar password"}
              title={showPassword ? "Ocultar password" : "Mostrar password"}
              data-testid="login-password-toggle"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 border border-[#E50000]/50 bg-[#E50000]/10 text-[#FF6B6B] text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="kw-btn-primary w-full" data-testid="login-submit">
            {loading ? "A entrar…" : "Entrar"}
          </button>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#737373]">
            <div className="flex-1 h-px bg-[#222]" /> ou <div className="flex-1 h-px bg-[#222]" />
          </div>
          <button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full border border-[#222] hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="login-google">
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.5 6.5 0 0 1 5.5 12c0-.73.12-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.47 1.18 4.96l3.66-2.86z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Entrar com Google
          </button>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="link-forgot" className="text-xs text-[#A3A3A3] hover:text-white uppercase tracking-widest font-bold">
              Esqueci-me da palavra-passe
            </Link>
          </div>

          <div className="mt-6 text-sm text-[#A3A3A3] text-center">
            Ainda não tem conta?{" "}
            <Link to="/register" data-testid="link-register" className="text-[#E50000] hover:underline font-semibold">
              Registar
            </Link>
            <div className="text-xs text-[#737373] mt-2">
              Acesso reservado a membros KW Lead com perfil MBTI atribuído pelo admin.
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
